import { useMemo } from 'react';
import { SharedValue } from 'react-native-reanimated';

import { useEventTime } from '@/components/EventTime';
import { convertSbkEventToHudEvent } from '@/components/event-hud/utils';
import { EventSheetTitle } from '@/components/event-sheet/EventSheetTitle';
import { Event } from '@/feature/event-details-sbk/types';
import { useSbkHudHeight } from '@/feature/sbk-event-sheets/hooks/use-sbk-hud-height';
import { useTeamScore } from '@/hooks/use-sbk-event-details-cache';
import { getPickemLeague } from '@/utils/league-utils';
import { getSeparatorSymbol, getTeamDisplayName, getTeamOrder, shouldShowLeagueName } from '@/utils/sport-utils';

type SbkEventSheetTitleProps = {
    event: Event;
    scrollOffset: SharedValue<number>;
};

export const SbkEventSheetTitle = ({ event, scrollOffset }: SbkEventSheetTitleProps) => {
    const { eventStatus, hudHeight } = useSbkHudHeight(event);
    const isLive = eventStatus === 'LIVE';
    const hudEvent = convertSbkEventToHudEvent(event);
    const eventTime = useEventTime(event);
    const homeScore = useTeamScore(event, 'home');
    const awayScore = useTeamScore(event, 'away');
    const pickemLeague = getPickemLeague(event);
    const sportName = event.sport.name.toLowerCase();

    const teamOrder = getTeamOrder(event.sport.name);
    const [firstSide, secondSide] = teamOrder;

    const eventTitle = useMemo(() => {
        const separator = getSeparatorSymbol(sportName);
        const teams = { away: event.away_team, home: event.home_team };

        return (
            getTeamDisplayName(teams[firstSide], sportName) +
            ` ${separator} ` +
            getTeamDisplayName(teams[secondSide], sportName)
        );
    }, [event, sportName, firstSide, secondSide]);

    const eventDetails = useMemo(
        () => ({
            matchDetails: eventTime,
            league: pickemLeague,
            eventTime: eventTime,
            isLive: isLive,
        }),
        [eventTime, pickemLeague, isLive]
    );

    const teamDetails = useMemo(() => {
        const scores = { away: awayScore, home: homeScore };
        const hudTeams = { away: hudEvent?.awayTeam, home: hudEvent?.homeTeam };
        return {
            awayTeam: hudTeams[firstSide],
            homeTeam: hudTeams[secondSide],
            awayScore: scores[firstSide],
            homeScore: scores[secondSide],
        };
    }, [hudEvent, awayScore, homeScore, firstSide, secondSide]);

    const eventSubTitle = useMemo(
        () => (shouldShowLeagueName(sportName) ? event.league.name : undefined),
        [event, sportName]
    );

    return (
        <EventSheetTitle
            hasTeams
            eventTitle={eventTitle}
            eventSubTitle={eventSubTitle}
            showGameTracker={isLive}
            pamEvent={event}
            scrollOffset={scrollOffset}
            hudHeight={hudHeight}
            teamDetails={teamDetails}
            eventDetails={eventDetails}
        />
    );
};
